import React, { useState, useEffect, useMemo } from "react";
import api from '../../api/axios'; // Use authenticated api instance instead of direct axios
import {
  LifebuoyIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  PaperAirplaneIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  return date.toLocaleDateString();
};

const SuccessMessage = ({ onReset }) => (
  <div className="text-center p-8 bg-green-50 rounded-2xl border-2 border-dashed border-green-200">
    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <h2 className="text-2xl font-display-bold text-gray-800 mb-2">Request Submitted!</h2>
    <p className="text-gray-600 font-body mb-6">
      Thank you for reaching out. Our support team will get back to you within 24 hours.
    </p>
    <button
      onClick={onReset}
      className="bg-gradient-to-r from-palamuru-saffron to-palamuru-gold text-white font-body-semibold py-2 px-6 rounded-xl shadow-soft hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300"
    >
      Submit Another Request
    </button>
  </div>
);

const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  const [sort, setSort] = useState('newest');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use the authenticated api instance instead of direct axios
        const { data } = await api.get("/support");
        // Fix: Access the nested data property and ensure it's always an array
        const ticketsData = data.data || data || [];
        setTickets(Array.isArray(ticketsData) ? ticketsData : []);
      } catch (err) {
        console.error("Failed to fetch support tickets:", err);
        setError("Could not load support tickets. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/support', formData);
      // Fix: Access the nested data property and ensure prev is always an array
      const newTicket = response.data.data || response.data;
      setTickets(prev => {
        const prevTickets = Array.isArray(prev) ? prev : [];
        return [newTicket, ...prevTickets];
      });
      setShowForm(false);
      setFormData({ subject: '', description: '', priority: 'medium', category: 'general' });
    } catch (err) {
      console.error("Failed to create ticket:", err);
      setError("Could not create support ticket. Please try again.");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Resolved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'In Progress':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'Open':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'Closed':
        return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter and sort tickets
  const filteredAndSortedTickets = useMemo(() => {
    let filtered = tickets;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = tickets.filter(ticket => {
        if (statusFilter === 'open') {
          return ticket.status === 'Open';
        } else if (statusFilter === 'resolved') {
          return ticket.status === 'Resolved';
        }
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      
      if (sort === 'newest') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });

    return filtered;
  }, [tickets, sort, statusFilter]);

  // Handle view details click
  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
  };

  // Close details modal
  const closeDetailsModal = () => {
    setSelectedTicket(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Support Tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Support</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen font-body">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-display-bold text-gray-900 mb-2">Support Center</h1>
          <p className="text-lg text-gray-600">
            Get help with your project, ask questions, and track your support tickets.
          </p>
        </div>

        {/* Sorting and Filter Row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-500 font-medium">Sort by:</label>
            <select
              className="border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              value={sort} onChange={e => setSort(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-500 font-medium">Status:</label>
            <select
              className="border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        {/* New Ticket Button */}
        {filteredAndSortedTickets.length > 0 && (
          <div className="mb-8">
            <button
              onClick={() => setShowForm(true)}
              className="bg-palamuru-blue text-white px-6 py-3 rounded-xl hover:bg-palamuru-blue-dark transition-colors font-semibold flex items-center gap-2"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
              Create New Ticket
            </button>
          </div>
        )}

        {/* Support Tickets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedTickets.length > 0 ? (
            filteredAndSortedTickets.map(ticket => (
              <div
                key={ticket.id}
                className="bg-white rounded-xl shadow-md hover:shadow-lg p-6 flex flex-col justify-between min-h-[220px] transition-all duration-200 cursor-pointer"
              >
                {/* Ticket ID */}
                <div className="text-xs text-gray-400 font-mono mb-2 font-semibold tracking-wider">#{`TCK-${String(ticket.id).slice(-4).padStart(4, '0')}`}</div>
                {/* Subject & Description */}
                <div className="mb-4">
                  <div className="text-base font-bold text-gray-800 mb-1">
                    {ticket.subject.charAt(0).toUpperCase() + ticket.subject.slice(1).toLowerCase()}
                  </div>
                  <div className="text-sm text-gray-500">{ticket.description}</div>
                </div>
                {/* Bottom Row */}
                <div className="flex items-center justify-between mt-auto pt-2">
                  <div className="flex items-center gap-2">
                    {/* Priority Badge */}
                    <span className={
                      `px-2 py-0.5 rounded-full text-xs font-semibold capitalize flex items-center gap-1 ` +
                      (ticket.priority === 'high' ? 'bg-red-100 text-red-700' :
                        ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700')
                    }>
                      {ticket.priority === 'high' && '⚡'}
                      {ticket.priority === 'medium' && '⚠️'}
                      {ticket.priority === 'low' && '📌'}
                      {ticket.priority}
                    </span>
                    {/* Category */}
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      {ticket.category === 'technical' && '🔧'}
                      {ticket.category === 'billing' && '🧾'}
                      {ticket.category === 'project' && '🏗️'}
                      {ticket.category === 'general' && '📋'}
                      {ticket.category}
                    </span>
                    {/* Created Date */}
                    <span className="text-xs text-gray-400">
                      {new Date(ticket.created_at).toLocaleString('en-IN', { 
                        day: '2-digit', 
                        month: 'short', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </span>
                  </div>
                  {/* Status Badge */}
                  <span className={
                    `px-3 py-0.5 rounded-full text-xs font-semibold capitalize ` +
                    (ticket.status === 'Resolved' ? 'bg-green-100 text-green-700' :
                      ticket.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                      ticket.status === 'Open' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-500')
                  }>
                    {ticket.status}
                  </span>
                </div>
                {/* View Details Button */}
                <button
                  className="mt-4 px-4 py-2 rounded-md font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 hover:shadow-sm transition-all duration-200 w-full"
                  onClick={() => handleViewDetails(ticket)}
                >
                  View Details
                </button>
                {/* Response (if any) */}
                {ticket.response && (
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Response:</h4>
                    <p className="text-gray-700">{ticket.response}</p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-16">
              {/* Illustration (optional) */}
              <div className="mb-6">
                <svg width="80" height="80" fill="none" viewBox="0 0 80 80">
                  <rect x="10" y="30" width="60" height="35" rx="8" fill="#F3F4F6" />
                  <rect x="20" y="40" width="40" height="15" rx="4" fill="#E0E7EF" />
                  <rect x="28" y="47" width="24" height="3" rx="1.5" fill="#C7D2FE" />
                  <rect x="32" y="52" width="16" height="3" rx="1.5" fill="#C7D2FE" />
                  <rect x="35" y="35" width="10" height="3" rx="1.5" fill="#A5B4FC" />
                  <rect x="36" y="60" width="8" height="8" rx="4" fill="#6366F1" />
                </svg>
              </div>
              <h2 className="text-2xl font-display-bold text-gray-800 mb-2">Need help with your home project?</h2>
              <p className="text-gray-600 text-center max-w-xl mb-6">
                We're here to answer questions, track concerns, and keep your project smooth. You can raise a ticket for any issue, big or small.
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="bg-palamuru-blue text-white px-8 py-3 rounded-xl text-lg font-semibold shadow hover:bg-palamuru-blue-dark transition-colors mb-8"
              >
                Create Support Ticket
              </button>
              {/* Example questions */}
              <div className="flex flex-col md:flex-row gap-6 mt-2">
                <div className="flex items-center gap-3 bg-gray-100 rounded-lg px-4 py-3">
                  <LifebuoyIcon className="h-6 w-6 text-blue-400" />
                  <span className="text-gray-700 text-sm">How do I track my project's progress?</span>
                </div>
                <div className="flex items-center gap-3 bg-gray-100 rounded-lg px-4 py-3">
                  <TicketIcon className="h-6 w-6 text-green-400" />
                  <span className="text-gray-700 text-sm">I have a billing or payment question.</span>
                </div>
                <div className="flex items-center gap-3 bg-gray-100 rounded-lg px-4 py-3">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-pink-400" />
                  <span className="text-gray-700 text-sm">Can I request a site visit or update?</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* New Ticket Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Support Ticket</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    >
                      <option value="general">General</option>
                      <option value="technical">Technical</option>
                      <option value="billing">Billing</option>
                      <option value="project">Project Related</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors flex items-center gap-2"
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                    Submit Ticket
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Details Modal */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Ticket Details</h2>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Ticket ID and Status */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500 font-mono">#{`TCK-${String(selectedTicket.id).slice(-4).padStart(4, '0')}`}</div>
                  <span className={
                    `px-3 py-1 rounded-full text-sm font-semibold capitalize ` +
                    (selectedTicket.status === 'Resolved' ? 'bg-green-100 text-green-700' :
                      selectedTicket.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                      selectedTicket.status === 'Open' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-500')
                  }>
                    {selectedTicket.status}
                  </span>
                </div>

                {/* Subject */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Subject</h3>
                  <p className="text-gray-700">{selectedTicket.subject.charAt(0).toUpperCase() + selectedTicket.subject.slice(1).toLowerCase()}</p>
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {/* Ticket Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Priority</h4>
                    <span className={
                      `px-3 py-1 rounded-full text-sm font-semibold capitalize flex items-center gap-1 w-fit ` +
                      (selectedTicket.priority === 'high' ? 'bg-red-100 text-red-700' :
                        selectedTicket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700')
                    }>
                      {selectedTicket.priority === 'high' && '⚡'}
                      {selectedTicket.priority === 'medium' && '⚠️'}
                      {selectedTicket.priority === 'low' && '📌'}
                      {selectedTicket.priority}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Category</h4>
                    <div className="flex items-center gap-1 text-gray-700 capitalize">
                      {selectedTicket.category === 'technical' && '🔧'}
                      {selectedTicket.category === 'billing' && '🧾'}
                      {selectedTicket.category === 'project' && '🏗️'}
                      {selectedTicket.category === 'general' && '📋'}
                      {selectedTicket.category}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Created</h4>
                    <p className="text-gray-700">{new Date(selectedTicket.created_at).toLocaleString('en-IN', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Last Updated</h4>
                    <p className="text-gray-700">{new Date(selectedTicket.updated_at || selectedTicket.created_at).toLocaleString('en-IN', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}</p>
                  </div>
                </div>

                {/* Response */}
                {selectedTicket.response && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Response</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.response}</p>
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={closeDetailsModal}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage; 